import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/LanguageContext';
import { loginRequest, useApi } from '../hooks/useApi';
import ThemeToggle from '../components/Shared/ThemeToggle';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, login } = useAuth();
  const { t } = useTranslation();
  const { api } = useApi();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const redirectTo = searchParams.get('redirect_to');
  const toolId = searchParams.get('tool_id');
  const showRedirectHint = !!(redirectTo && toolId);

  // Wenn schon eingeloggt → direkt weiterleiten
  useEffect(() => {
    if (token && !redirectTo) {
      navigate('/', { replace: true });
    }
  }, [token, redirectTo, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const data = await loginRequest(email.trim(), password);
      if (!data?.access_token) {
        throw new Error(t('login.error.failed'));
      }
      login(data.access_token);

      // SSO-Redirect: ?redirect_to=URL&tool_id=X
      if (redirectTo && toolId) {
        try {
          const exchange = await api(
            `auth/token/exchange?tool_id=${encodeURIComponent(toolId)}&env=prod`,
            { method: 'POST', body: {} }
          );
          let url = redirectTo;
          if (exchange?.tool_token) {
            const sep = url.includes('?') ? '&' : '?';
            url = `${url}${sep}platform_token=${encodeURIComponent(exchange.tool_token)}`;
          }
          window.location.href = url;
          return;
        } catch {
          // Token-Exchange fehlgeschlagen → trotzdem zur Ziel-URL ohne Token
          window.location.href = redirectTo;
          return;
        }
      }

      navigate('/', { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail
        || err?.response?.data?.message
        || err?.message
        || t('login.error.failed');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="screen-login">
      <div className="login-card">
        <div className="login-logo">
          <div className="login-logo-icon">
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
              <path d="M10 2L17.32 6V14L10 18L2.68 14V6L10 2Z" stroke="white" strokeWidth="1.5" strokeLinejoin="round" />
              <path d="M10 6L14 8.5V13.5L10 16L6 13.5V8.5L10 6Z" fill="white" fillOpacity="0.4" />
            </svg>
          </div>
          <div>
            <div className="login-logo-text">{t('login.logo.product')}</div>
            <div className="login-logo-sub">{t('login.logo.brand')}</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div className="login-title">{t('login.title')}</div>
            <div className="login-subtitle">{t('login.subtitle')}</div>
          </div>
          <ThemeToggle />
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="login-email">{t('login.email')}</label>
            <input
              id="login-email"
              type="email"
              className="form-input"
              placeholder="name@secaas.it"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="login-password">{t('login.password')}</label>
            <input
              id="login-password"
              type="password"
              className="form-input"
              placeholder="••••••••"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%', marginTop: 8 }}
            disabled={submitting}
          >
            {submitting ? t('login.submitting') : t('login.submit')}
          </button>

          <div style={{ marginTop: 12, textAlign: 'center', fontSize: 13 }}>
            <Link to="/forgot-password">{t('login.forgot', 'Passwort vergessen?')}</Link>
          </div>

          {error && <div className="login-error">{error}</div>}

          {showRedirectHint && (
            <div className="login-redirect-hint">
              {t('login.redirectHint', { tool: toolId })}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
